/**
 * Display name from user or agent: prefers firstName + lastName, fallback to fullName/name.
 * For agents, never show "User" — use agent name or "Agent".
 */
export function getDisplayName(userOrAgent) {
  const u = userOrAgent?.user ?? userOrAgent;
  const firstLast = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
  const name = firstLast || u?.fullName || userOrAgent?.fullName || userOrAgent?.name || 'Agent';
  if (typeof name === 'string' && name.trim().toLowerCase() === 'user') return 'Agent';
  return name;
}

/** Status for display: never show "Imported" — show "Pending" instead. */
export function getDisplayStatus(status) {
  if (status == null || status === '') return 'Pending';
  if (String(status).toLowerCase() === 'imported') return 'Pending';
  const s = String(status);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** For any field value: never show "Imported" — show fallback instead (e.g. "—", "Not provided"). */
/** Strip import suffix from valuation for display (e.g. LMKMA02003025-LMT → LMKMA02003025). */
/** Primary bill label: account number / New-ID first, else valuation / bill. */
export function getPropertyBillPrimary(prop) {
  const p = prop || {};
  const account = p.accountNumber ?? p.account_number;
  if (account != null && String(account).trim() !== '') return String(account).trim();
  const newId = p.newId ?? p.new_id;
  if (newId != null && String(newId).trim() !== '') return String(newId).trim();
  const val = formatValuationNo(p.valuationNo ?? p.valuation_no);
  return val || p.billNo || p.bill_no || '—';
}

/** Secondary line: valuation number when primary is account / New-ID. */
export function getPropertyBillSecondary(prop) {
  const p = prop || {};
  const val = formatValuationNo(p.valuationNo ?? p.valuation_no);
  if (!val) return null;
  const primary = getPropertyBillPrimary(p);
  return String(val).trim() !== primary ? val : null;
}

export function formatValuationNo(value) {
  if (value == null || value === '') return value;
  return String(value).replace(/-LMT$/i, '');
}

export function hideImported(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  if (String(value).trim().toLowerCase() === 'imported') return fallback;
  return value;
}

/** Address line from prop: never show "imported". */
export function getDisplayAddress(prop, fallback = '—') {
  if (!prop) return fallback;
  const street = hideImported(prop.streetName, '');
  const house = hideImported(prop.houseNumber, '');
  const loc = hideImported(prop.location);
  const part = [street, house].filter((x) => x).join(' ').trim();
  return part || loc || fallback;
}
